'use client';

import { ReactNode, createContext, useContext, useReducer } from 'react';

interface CookiesState {
  [key: string]: string; // Allow additional cookie values
}

type CookiesAction = { type: 'SET_COOKIE'; key: string; value: string };

const CookiesValueContext = createContext<CookiesState | undefined>(undefined);
const CookiesDispatchContext = createContext<
  React.Dispatch<CookiesAction> | undefined
>(undefined);

const reducer = (state: CookiesState, action: CookiesAction): CookiesState => {
  switch (action.type) {
    case 'SET_COOKIE':
      return { ...state, [action.key]: action.value };
    default:
      throw new Error(`Unknown action: ${action.type}`);
  }
};

const initialState: CookiesState = {
  token: '',
};

const CookiesProvider = ({
  children,
  initialCookies = initialState,
}: {
  children: ReactNode;
  initialCookies?: CookiesState;
}) => {
  const [cookies, dispatch] = useReducer(reducer, initialCookies);

  return (
    <CookiesValueContext.Provider value={cookies}>
      <CookiesDispatchContext.Provider value={dispatch}>
        {children}
      </CookiesDispatchContext.Provider>
    </CookiesValueContext.Provider>
  );
};

const useCookies = (): CookiesState => {
  const cookies = useContext(CookiesValueContext);
  if (cookies === undefined)
    throw new Error('useCookies must be used within a CookiesProvider');
  return cookies;
};

const useSetCookies = (): React.Dispatch<CookiesAction> => {
  const dispatch = useContext(CookiesDispatchContext);
  if (dispatch === undefined)
    throw new Error('useSetCookies must be used within a CookiesProvider');
  return dispatch;
};

export { CookiesProvider, useCookies, useSetCookies };
